/* @flow */

import * as React from 'react';
import { get } from 'lodash/fp';
import glamorous from 'glamorous';
import colorUtils from 'color';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import AnimatedRowGroup from 'app/components/commons/AnimatedRowGroup';
import t from 'app/utils/translate';
import { FRIEND, NOT_FRIEND } from 'app/constants/friendStatusTypes';
import LeaderRow from './LeaderRow';

type Props = {
  competitors: Array<Object>,
  displayCountry?: boolean,
  displayPlayersCount: boolean,
  dynamicColumn: string,
  filterHash: string,
  isInProgress?: boolean,
  isLoading?: boolean,
  isTicketChallenge?: boolean,
  onEntrySelect: string => void,
  opponentEntryId?: string,
  showProjected: boolean,
  userEntryId?: string
};

const NoEntries = glamorous.div(({ theme }) => ({
  backgroundColor: colorUtils(theme.color.basis1)
    .alpha(0.5)
    .toString(),
  color: theme.color.basis6,
  marginTop: 10,
  padding: 30,
  textAlign: 'center'
}));

const Wrapper = glamorous.div({
  position: 'relative'
});

const StyledAnimatedRowGroup = glamorous(AnimatedRowGroup)({
  position: 'relative'
});

const StyledLeaderRow = glamorous(LeaderRow)({
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0
});

class LeaderBoardTable extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    displayCountry: false,
    isInProgress: false,
    isLoading: false,
    isTicketChallenge: false,
    opponentEntryId: undefined,
    userEntryId: undefined
  };

  render() {
    const {
      competitors,
      displayCountry,
      displayPlayersCount,
      dynamicColumn,
      filterHash,
      isInProgress,
      isLoading,
      isTicketChallenge,
      onEntrySelect,
      opponentEntryId,
      showProjected,
      userEntryId
    } = this.props;

    // rendering just the loader here forces the AnimatedRowGroup to unmount.
    // This is good! it means when we fetch new data via a filter we won't see
    // any live leaderboard animations
    //
    // when we poll for leaderboard updates it happens silently and we don't
    // receive the `isLoading` flag.
    //
    if (isLoading) {
      return (
        <Wrapper>
          <FriendsLoader showOverlay>{t('Fetching entries...')}</FriendsLoader>
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        {competitors.length === 0 ? (
          <NoEntries key="noentries">{t("Couldn't find any entries")}</NoEntries>
        ) : (
          <StyledAnimatedRowGroup
            key={filterHash} /* re-mount component to avoid animating a new list */
          >
            {competitors.map(entry => (
              <StyledLeaderRow
                key={entry.id}
                avatar={entry.avatar}
                countryImage={entry.countryImage}
                countryName={entry.countryName}
                displayCountry={displayCountry}
                displayPlayersCount={displayPlayersCount}
                dynamicColumn={dynamicColumn}
                friendStatus={entry.isFriend ? FRIEND : NOT_FRIEND}
                id={entry.id}
                isHighlighted={entry.id === userEntryId}
                isInProgress={isInProgress}
                isMarked={entry.id === opponentEntryId}
                isTicketChallenge={isTicketChallenge}
                isUltimateChampion={entry.isUltimateChampion}
                isVIP={entry.isVIP}
                name={entry.userName}
                onClick={entry.id !== userEntryId ? onEntrySelect : undefined}
                playersCount={entry.playersCount}
                prize={showProjected ? entry.prizeProjected : entry.prize}
                showCurrency={get('prizeType', entry) !== 'text'}
                rank={showProjected ? entry.rankProjected : entry.rank}
                score={showProjected ? entry.scoreProjected : entry.score}
              />
            ))}
          </StyledAnimatedRowGroup>
        )}
      </Wrapper>
    );
  }
}

export default LeaderBoardTable;
